import { createColumnHelper } from '@tanstack/react-table';
import { Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { MrsLink } from '@/components/MrsLink';
import type { MaterialRequestDto } from '../types';
import { useMaterialRequestStore } from '../store';

const columnHelper = createColumnHelper<MaterialRequestDto>();

// Get store action directly without creating subscription per row
const handleViewDetails = (request: MaterialRequestDto) => {
  useMaterialRequestStore.getState().openDetailSheet(request);
};

export const picklistCreationColumns = [
  // Checkbox column for row selection
  columnHelper.display({
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
        onClick={(e) => e.stopPropagation()}
      />
    ),
    enableSorting: false,
    enableHiding: false,
  }),
  columnHelper.accessor('mrsNumber', {
    header: 'MRS Number',
    cell: (info) => {
      const mrsNumber = info.getValue();
      if (!mrsNumber) return <span className="font-medium">-</span>;
      return (
        <MrsLink
          mrsId={info.row.original.id}
          mrsNumber={mrsNumber}
          className="font-medium"
        />
      );
    },
  }),
  columnHelper.accessor('requestorFullName', {
    header: 'Requestor',
    cell: (info) => info.getValue() || '-',
  }),
  columnHelper.accessor('requestedAt', {
    header: 'Requested Date',
    cell: (info) => {
      const value = info.getValue();
      return value
        ? new Date(value).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })
        : '-';
    },
  }),
  columnHelper.accessor('approvedBy', {
    header: 'Approved By',
    cell: (info) => info.getValue() || '-',
  }),
  columnHelper.accessor((row) => row.items.length, {
    id: 'itemCount',
    header: 'Items',
    cell: (info) => (
      <Badge variant="secondary">{info.getValue()} item(s)</Badge>
    ),
  }),
  columnHelper.display({
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => (
      <Button
        variant="ghost"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          handleViewDetails(row.original);
        }}
        title="View Details"
      >
        <Eye className="h-4 w-4" />
      </Button>
    ),
  }),
];
